import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Users, ArrowUpRight, Download, Loader2, Globe, FileSpreadsheet, Monitor, Code2, ExternalLink, ShoppingCart } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPE_ICON = { WEB: Globe, EXCEL: FileSpreadsheet, WINDOWS: Monitor, HTML: Code2 };
const TYPE_LABEL = { WEB: "Application Web", EXCEL: "Modèle Excel", WINDOWS: "Logiciel Windows", HTML: "Application HTML" };

export default function ProductDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { add: addToCart } = useCart();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [owned, setOwned] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/tools/${slug}`).then(({ data }) => setTool(data)).catch(() => setTool(false)).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!user || !tool) return;
    if (tool.free) { setOwned(true); return; }
    axios.get(`${API}/me/entitlements`).then(({ data }) => setOwned((data.lookup_keys || []).includes(tool.lookup_key))).catch(() => {});
  }, [user, tool]);

  const buy = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/payments/checkout`, { lookup_key: tool.lookup_key, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error("Impossible d'ouvrir le paiement."); } finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/tools/${slug}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `${tool.name}.${tool.file_ext || "zip"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Téléchargement impossible."); } finally { setBusy(false); }
  };

  const viewOnline = async () => {
    const win = window.open("", "_blank");
    setBusy(true);
    try {
      const res = await axios.get(`${API}/tools/${slug}/view`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      if (win) { win.location.href = url; } else { window.open(url, "_blank"); }
    } catch (e) { win?.close(); toast.error("Ouverture impossible."); } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen bg-ink-900 flex items-center justify-center"><Loader2 className="text-gold animate-spin" size={40} /></div>;
  if (!tool) return (
    <div className="min-h-screen bg-ink-900 grain text-paper flex items-center justify-center text-center px-6">
      <div><p className="font-display text-3xl">Outil introuvable.</p><Link to="/outils-apps" className="inline-flex items-center gap-2 rounded-full bg-gold text-ink px-6 py-3 label-mono mt-6">Retour au catalogue</Link></div>
    </div>
  );

  const Icon = TYPE_ICON[tool.tool_type] || Globe;

  return (
    <>
      <header className="relative bg-ink-900 text-paper grain pt-32 pb-16 md:pt-44 md:pb-24 overflow-hidden" data-testid="product-detail">
        {tool.has_cover && <img src={`${API}/tools/${tool.slug}/cover`} alt={tool.name} className="absolute inset-0 w-full h-full object-cover opacity-20" />}
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/70 to-ink-900/40" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10">
          <Link to="/outils-apps" className="inline-flex items-center gap-2 label-mono text-paper/70 hover:text-gold transition-colors mb-8"><ArrowLeft size={14} /> Tous les outils</Link>
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5 label-mono text-ink bg-gold px-3 py-1.5 rounded-full"><Icon size={12} /> {TYPE_LABEL[tool.tool_type]}</span>
                <span className="label-mono text-gold">{tool.category}</span>
              </div>
              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="font-display text-5xl md:text-7xl font-semibold mt-6 tracking-tight leading-[0.9]">{tool.name}</motion.h1>
              {tool.tagline && <p className="text-paper/75 text-xl mt-5 max-w-2xl">{tool.tagline}</p>}
            </div>
            <div className="lg:col-span-4">
              <div className="rounded-sm border hairline-light bg-white/5 backdrop-blur p-6">
                <div className="label-mono text-steel">{owned ? "Votre licence" : "Prix"}</div>
                <div className="font-display text-3xl font-semibold mt-1 text-gold">{tool.free ? "Gratuit" : `${tool.price} €`}</div>
                {owned ? (
                  <>
                    <button onClick={download} disabled={busy} data-testid="product-download" className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-ink px-6 py-3.5 label-mono hover:bg-paper transition-colors mt-5 disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <>Télécharger <Download size={15} /></>}</button>
                    {tool.tool_type === "HTML" && (
                      <button onClick={viewOnline} disabled={busy} data-testid="product-view-online" className="group w-full inline-flex items-center justify-center gap-2 rounded-full border border-gold/60 text-gold px-6 py-3.5 label-mono hover:bg-gold hover:text-ink transition-colors mt-3 disabled:opacity-60">Ouvrir en ligne <ExternalLink size={15} /></button>
                    )}
                  </>
                ) : (
                  <>
                    <button onClick={buy} disabled={busy} data-testid="product-buy" className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-ink px-6 py-3.5 label-mono hover:bg-paper transition-colors mt-5 disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <>Acheter maintenant <ArrowUpRight size={15} className="group-hover:rotate-45 transition-transform" /></>}</button>
                    <button onClick={() => addToCart({ key: tool.lookup_key, kind: "tool", name: tool.name, price: tool.price, slug: tool.slug })} data-testid="product-add-cart" className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-paper/30 px-6 py-3.5 label-mono hover:bg-paper hover:text-ink transition-colors mt-3">Ajouter au panier <ShoppingCart size={15} /></button>
                  </>
                )}
                <Link to="/contact" className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-paper/30 px-6 py-3.5 label-mono hover:bg-paper hover:text-ink transition-colors mt-3">Demander une démo</Link>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="bg-paper py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            {(tool.description || tool.short) && (<Reveal><span className="label-mono text-gold">Présentation</span><h2 className="font-display text-2xl md:text-3xl font-medium mt-4 leading-[1.15] tracking-tight">{tool.description || tool.short}</h2></Reveal>)}
            {tool.features?.length > 0 && (<>
              <Reveal delay={0.1}><h3 className="font-display text-2xl font-semibold mt-14 mb-6">Fonctionnalités</h3></Reveal>
              <Stagger className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {tool.features.map((f) => (<StaggerItem key={f} className="flex items-start gap-3 py-2 border-b hairline"><Check size={16} className="text-gold mt-1 shrink-0" /><span className="text-ink/80">{f}</span></StaggerItem>))}
              </Stagger>
            </>)}
          </div>
          <div className="lg:col-span-5">
            {tool.for_whom?.length > 0 && (
              <Reveal delay={0.15}>
                <div className="rounded-sm border hairline bg-white p-8">
                  <div className="flex items-center gap-2 mb-6"><Users size={18} className="text-gold" /><h3 className="font-display text-xl font-semibold">Pour qui ?</h3></div>
                  <ul className="space-y-3">{tool.for_whom.map((w) => (<li key={w} className="flex items-center gap-3 text-ink/75"><span className="w-1.5 h-1.5 rounded-full bg-gold" /> {w}</li>))}</ul>
                  <div className="mt-8 pt-6 border-t hairline grid grid-cols-2 gap-4 label-mono text-ink/50">
                    <div><div className="text-ink/40">Type</div><div className="text-ink mt-1">{tool.tool_type}</div></div>
                    <div><div className="text-ink/40">Catégorie</div><div className="text-ink mt-1">{tool.category}</div></div>
                  </div>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
